import { Directive, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Observable, of, switchMap } from "rxjs";
import swal from "sweetalert2";

import {
  ComentarioIso,
  IncidenciaIsoComun,
  OpcionIso,
  PorquesIso,
  RespuestaIso,
  ServicioIncidenciasIso,
  porquesFormularioIso,
} from "./incidencias-iso.comun";

export type ModoFormularioIso = "alta" | "edicion" | "seguimiento";

/** Datos con los que se abre el formulario. */
export interface DatosFormularioIso<TIncidencia> {
  mode: ModoFormularioIso;
  idIncidenciaIa?: number;
  incidencia?: TIncidencia;
}

/** Textos que cambian entre el formulario de incidencias IA y el de seguridad. */
export interface TextosFormularioIso {
  /** Texto del botón mientras se guarda. */
  guardando: string;
  /** Aviso cuando faltan los primeros cinco porqués. */
  faltanCincoPorques: string;
}

/**
 * Formulario de alta, edición y seguimiento de una incidencia IA o de seguridad: modo y permisos de
 * captura, observaciones, porqués de la causa / origen y la petición de guardado (con el comentario
 * pendiente antes de actualizar). Cada pantalla valida sus campos y decide qué hace al guardar.
 */
@Directive()
export abstract class FormularioIncidenciaIsoBase<TIncidencia extends IncidenciaIsoComun> {
  abstract readonly gravedadOptions: OpcionIso[];
  abstract readonly prioridadOptions: OpcionIso[];
  readonly minFechaPlaneada = this.inicioDia(new Date());

  abstract incidencia: TIncidencia;
  porques: string[] = ["", "", "", "", ""];
  comentarios: ComentarioIso[] = [];
  nuevoComentario = "";
  panelDerecho: "observaciones" | "porques" = "observaciones";
  cargando = false;
  guardando = false;
  error = "";
  advertencia = "";
  data = inject<DatosFormularioIso<TIncidencia>>(MAT_DIALOG_DATA);
  protected readonly dialogRef = inject(MatDialogRef);
  protected abstract readonly incidenciasService: ServicioIncidenciasIso<TIncidencia>;
  protected abstract readonly textos: TextosFormularioIso;

  get esSeguimiento(): boolean {
    return this.data.mode === "seguimiento";
  }

  get esEdicion(): boolean {
    return this.data.mode === "edicion";
  }

  get muestraSeguimiento(): boolean {
    return this.esSeguimiento || (this.esEdicion && this.incidencia.estadoEvento !== "ABIERTO");
  }

  get muestraApertura(): boolean {
    return !this.muestraSeguimiento;
  }

  /** Estados en los que ya solo se pueden agregar comentarios. */
  abstract get soloLecturaCampos(): boolean;

  /** Estados en los que el formulario solo se consulta. */
  abstract get soloConsulta(): boolean;

  get textoBotonGuardar(): string {
    if (this.guardando) {
      return this.textos.guardando;
    }
    if (this.soloConsulta) {
      return "Solo consulta";
    }
    if (this.soloLecturaCampos) {
      return "Guardar comentario";
    }
    if (this.muestraSeguimiento) {
      return "Guardar seguimiento";
    }
    return this.esEdicion ? "Actualizar" : "Guardar";
  }

  cancelar(): void {
    this.dialogRef.close({ refresh: true });
  }

  abrirPanelDerecho(panel: "observaciones" | "porques"): void {
    this.panelDerecho = panel;
  }

  agregarPorque(): void {
    if (this.soloLecturaCampos) {
      return;
    }
    this.porques.push("");
  }

  eliminarPorque(index: number): void {
    if (this.soloLecturaCampos || this.porques.length <= 5 || index < 5) {
      return;
    }
    this.porques.splice(index, 1);
  }

  getDescripcionGravedad(): string {
    return this.gravedadOptions.find((option) => option.value === this.incidencia.gravedad)?.description || "";
  }

  getDescripcionPrioridad(): string {
    return this.prioridadOptions.find((option) => option.value === this.incidencia.prioridad)?.description || "";
  }

  trackByPorque(index: number): number {
    return index;
  }

  trackByComentario(_index: number, comentario: ComentarioIso): number {
    return comentario.idComentarioIa || 0;
  }

  /**
   * Petición de guardado: con la incidencia en revisión solo guarda el comentario; si no, guarda el
   * comentario pendiente y después actualiza (edición o seguimiento) o da de alta la incidencia.
   */
  protected solicitudGuardar(): Observable<RespuestaIso<TIncidencia>> {
    if (this.soloLecturaCampos && !this.soloConsulta) {
      return this.incidenciasService.guardarComentario(this.crearComentarioRequest());
    }
    return this.guardarComentarioPendiente().pipe(
      switchMap(() =>
        this.esEdicion || this.esSeguimiento
          ? this.incidenciasService.actualizar(this.incidencia)
          : this.incidenciasService.guardar(this.incidencia),
      ),
    );
  }

  /** Si el guardado no trajo respuesta o trajo un error, lo muestra y devuelve true. */
  protected guardadoConError(res: RespuestaIso<TIncidencia> | null, mensaje: string): boolean {
    if (!res) {
      this.mostrarErrorGuardar(mensaje);
      this.guardando = false;
      return true;
    }
    if (res.codigoError) {
      this.mostrarErrorGuardar(res.mensajeError || mensaje);
      this.guardando = false;
      return true;
    }
    return false;
  }

  /** Avisa que se guardó (o la advertencia del backend) y al cerrar el aviso cierra el formulario. */
  protected avisarGuardadoYCerrar(titulo: string, texto: string, idIncidenciaIa?: number): void {
    swal.fire({
      title: titulo,
      text: this.advertencia || texto,
      timer: 5000,
      icon: this.advertencia ? "warning" : "success",
      showConfirmButton: true,
    }).then(() => {
      this.dialogRef.close({ refresh: true, idIncidenciaIa, advertencia: this.advertencia });
    });
  }

  protected mostrarErrorGuardar(mensaje: string): void {
    this.error = mensaje;
    swal.fire({
      title: "No se pudo guardar",
      text: mensaje,
      timer: 6000,
      icon: "error",
      showConfirmButton: true,
    });
  }

  protected validarPorquesSinSaltos(): boolean {
    const ultimoConTexto = this.porques.reduce((last, value, index) => value?.trim() ? index : last, -1);
    for (let i = 0; i <= ultimoConTexto; i++) {
      if (!this.porques[i]?.trim()) {
        this.error = "Los porqués deben capturarse en orden, sin saltos.";
        return false;
      }
    }
    return true;
  }

  protected validarCincoPorques(): boolean {
    for (let i = 0; i < 5; i++) {
      if (!this.porques[i]?.trim()) {
        this.error = this.textos.faltanCincoPorques;
        return false;
      }
    }
    return true;
  }

  /** Reparte la causa / origen en porqués (al menos cinco); un texto que no es JSON queda como el primero. */
  protected cargarPorques(): void {
    const porques = porquesFormularioIso(this.incidencia.causaOrigen);
    const total = Math.max(5, ...Object.keys(porques).map((key) => Number(key) || 0));
    this.porques = Array.from({ length: total }, (_item, index) => porques[String(index + 1)] || "");
    if (!Object.keys(porques).length && this.incidencia.causaOrigen?.trim()) {
      this.porques[0] = this.incidencia.causaOrigen.trim();
    }
  }

  protected prepararCausaOrigenParaGuardar(): void {
    if (!this.muestraSeguimiento || this.soloLecturaCampos) {
      return;
    }
    const porques = this.porques
      .map((value) => this.normalizarPorque(value))
      .filter((value) => !!value);
    this.porques = [...porques, ...new Array(Math.max(0, 5 - porques.length)).fill("")];
    this.incidencia.causaOrigen = porques.length
      ? JSON.stringify(
          porques.reduce<PorquesIso>((acc, value, index) => {
            acc[String(index + 1)] = value;
            return acc;
          }, {}),
        )
      : "";
  }

  /** Respuesta del porqué como se guarda (con su "Porque" al inicio). */
  protected abstract normalizarPorque(value?: string): string;

  protected prepararSeguimiento(): void {
    if (this.muestraSeguimiento && !this.incidencia.fechaPlaneadaResolucion) {
      this.incidencia.fechaPlaneadaResolucion = new Date();
    }
  }

  protected crearComentarioRequest(): ComentarioIso {
    return {
      idIncidenciaIa: this.incidencia.idIncidenciaIa,
      comentario: this.nuevoComentario.trim(),
    };
  }

  protected normalizarFechas(incidencia: TIncidencia): TIncidencia {
    return {
      ...incidencia,
      fechaApertura: this.toDate(incidencia.fechaApertura),
      fechaPlaneadaResolucion: this.toDate(incidencia.fechaPlaneadaResolucion),
      fechaAtencion: this.toDate(incidencia.fechaAtencion),
    };
  }

  protected toDate(value?: string | Date): Date | undefined {
    if (!value) {
      return undefined;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  protected inicioDia(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private guardarComentarioPendiente(): Observable<RespuestaIso<TIncidencia> | null> {
    if (!this.nuevoComentario.trim() || !this.incidencia.idIncidenciaIa) {
      return of(null);
    }
    return this.incidenciasService.guardarComentario(this.crearComentarioRequest());
  }
}
